import {
    BillingInterval,
    FEATURE_ADD_ON_CONFIG,
    FeatureAddOnCapabilityKey,
    FeatureAddOnKey,
    PLAN_CONFIG,
    PlanCapabilityKey,
    PlanKey,
    PlanPricing,
    PlanUsageLimit,
    PlanUsageResourceKey,
} from '@/constants';
import { UserPlan } from '@/types';

const FREE_PLAN_USAGE_LIMITS: Partial<Record<PlanUsageResourceKey, PlanUsageLimit>> = {
    investments: 3,
    memos: 3,
    folders: 2,
    pipelines: 1,
    teams: 1,
    dashboards: 2,
    lists: 2,
    dataSources: 2,
    subteamsPerTeam: 0,
};

const EDUCATION_PLAN_USAGE_LIMITS: Partial<Record<PlanUsageResourceKey, PlanUsageLimit>> = {
    investments: 'unlimited',
    memos: 'unlimited',
    folders: 'unlimited',
    pipelines: 'unlimited',
    teams: 'unlimited',
    dashboards: 'unlimited',
    lists: 'unlimited',
    dataSources: 'unlimited',
    watchlists: 'unlimited',
    tags: 'unlimited',
    subteamsPerTeam: 'unlimited',
};

const FREE_HISTORICAL_DATA_YEARS = 1;
const EDUCATION_HISTORICAL_DATA_YEARS = 10;

export const PLAN_USAGE_RESOURCE_LABELS: Record<PlanUsageResourceKey, string> = {
    investments: 'investments',
    memos: 'memos',
    folders: 'folders',
    pipelines: 'pipelines',
    teams: 'teams',
    watchlists: 'watchlists',
    tags: 'tags',
    dashboards: 'dashboards',
    lists: 'lists',
    dataSources: 'data sources',
    subteamsPerTeam: 'subteams per team',
};

export const PLAN_USAGE_RESOURCE_TO_PLAN_LABEL: Record<PlanUsageResourceKey, string> = {
    investments: 'Investments',
    memos: 'Memos',
    folders: 'Folders',
    pipelines: 'Pipelines',
    teams: 'Teams',
    watchlists: 'Watchlists',
    tags: 'Tags',
    dashboards: 'Dashboards',
    lists: 'Lists',
    dataSources: 'Data Sources',
    subteamsPerTeam: 'Subteams',
};

export function getPlan(planKey: PlanKey) {
    return PLAN_CONFIG[planKey];
}

export function getPlanKeyFromUserPlan(plan?: UserPlan): PlanKey | 'free' | 'education' | 'inactive' {
    if (!plan || plan === UserPlan.INACTIVE) {
        return 'inactive';
    }
    if (plan === UserPlan.FREE) {
        return 'free';
    }
    if (plan === UserPlan.ENTERPRISE) {
        return 'enterprise';
    }
    if (plan === UserPlan.EDUCATION_ANNUALLY || plan === UserPlan.EDUCATION_MONTHLY) {
        return 'education';
    }
    if (plan === UserPlan.PLUS_ANNUALLY || plan === UserPlan.PLUS_MONTHLY) {
        return 'plus';
    }
    if (plan === UserPlan.PRO_ANNUALLY || plan === UserPlan.PRO_MONTHLY) {
        return 'pro';
    }
    if (plan === UserPlan.PREMIUM_ANNUALLY || plan === UserPlan.PREMIUM_MONTHLY) {
        return 'premium';
    }

    return 'inactive';
}

export function getPlanLabelFromUserPlan(plan?: UserPlan): string {
    const planKey = getPlanKeyFromUserPlan(plan);

    if (planKey === 'free') {
        return 'Free';
    }
    if (planKey === 'education') {
        return 'Education';
    }
    if (planKey === 'inactive') {
        return 'Inactive';
    }

    return PLAN_CONFIG[planKey].label;
}

export function getPlanUsageLimits(plan?: UserPlan): Partial<Record<PlanUsageResourceKey, PlanUsageLimit>> {
    const planKey = getPlanKeyFromUserPlan(plan);

    if (planKey === 'free' || planKey === 'inactive') {
        return FREE_PLAN_USAGE_LIMITS;
    }
    if (planKey === 'education') {
        return EDUCATION_PLAN_USAGE_LIMITS;
    }

    return PLAN_CONFIG[planKey].usageLimits ?? {};
}

export function getPlanUsageLimit(plan: UserPlan | undefined, resource: PlanUsageResourceKey): PlanUsageLimit {
    return getPlanUsageLimits(plan)[resource] ?? 'unlimited';
}

export function getPlanHistoricalDataYears(plan?: UserPlan): number | 'unlimited' {
    const planKey = getPlanKeyFromUserPlan(plan);

    if (planKey === 'free' || planKey === 'inactive') {
        return FREE_HISTORICAL_DATA_YEARS;
    }
    if (planKey === 'education') {
        return EDUCATION_HISTORICAL_DATA_YEARS;
    }

    return PLAN_CONFIG[planKey].historicalDataYears ?? 'unlimited';
}

export function getFeatureAddOn(planKey: FeatureAddOnKey) {
    return FEATURE_ADD_ON_CONFIG[planKey];
}

export function getPlanPrice(planKey: PlanKey, interval: BillingInterval) {
    const plan = getPlan(planKey);

    if (!plan.pricing) {
        return null;
    }

    if (interval === 'monthly') {
        return plan.pricing.monthly.unitAmount;
    }

    return plan.pricing.annual.monthlyEquivalent;
}

export function getPlanAnnualTotal(planKey: PlanKey) {
    const plan = getPlan(planKey);
    if (!plan.pricing) {
        return null;
    }

    return plan.pricing.annual.yearlyAmount;
}

export function getPlanFeatures(planKey: PlanKey) {
    return getPlan(planKey).features ?? [];
}

export function getFeatureAddOnFeatures(planKey: FeatureAddOnKey) {
    return getFeatureAddOn(planKey).features ?? [];
}

export function supportsInterval(planKey: PlanKey, interval: BillingInterval) {
    const plan = getPlan(planKey);
    if (!plan.supportedIntervals) {
        return true;
    }
    return plan.supportedIntervals.includes(interval);
}

export function hasPlanCapability(planKey: PlanKey, capability: PlanCapabilityKey): boolean {
    const plan = PLAN_CONFIG[planKey];

    return Boolean(plan.capabilities?.[capability]);
}

export function getPlanCapabilities(planKey: PlanKey) {
    return PLAN_CONFIG[planKey].capabilities ?? {};
}

export function hasFeatureAddOnCapability(planKey: FeatureAddOnKey, capability: FeatureAddOnCapabilityKey): boolean {
    const plan = FEATURE_ADD_ON_CONFIG[planKey];

    return Boolean(plan.capabilities?.[capability]);
}

export function getFeatureAddOnCapabilities(planKey: FeatureAddOnKey) {
    return FEATURE_ADD_ON_CONFIG[planKey].capabilities ?? {};
}

export function getMonthlyEquivalent(pricing: PlanPricing | undefined, interval: BillingInterval): number | null {
    if (!pricing) {
        return null;
    }

    if (interval === 'monthly') {
        return pricing.monthly.unitAmount;
    }

    return pricing.annual.monthlyEquivalent;
}

export function getAnnualTotal(pricing?: PlanPricing): number | null {
    if (!pricing) {
        return null;
    }
    return pricing.annual.yearlyAmount;
}

export function getDerivedDiscount(pricing?: PlanPricing): number {
    if (!pricing) {
        return 0;
    }

    const monthly = pricing.monthly.unitAmount;
    const annual = pricing.annual.monthlyEquivalent;

    return Math.round((1 - annual / monthly) * 100);
}
